/**
 * QuietSelect — a closed vocabulary, as a row that is already a control.
 *
 * A THREE-MEMBER ENUM DOES NOT NEED A SEARCH BOX. `InlinePickerPopover` is the right answer for
 * witnesses, transactions and trackers — lists that grow, that are searched, and that can be
 * added to from inside the row. A registration type is `register | transfer | registered`,
 * forever; wrapping three fixed words in a filtered popover with a create row would promise an
 * openness the column does not have.
 *
 * IT IS A NATIVE `<select>`, DELIBERATELY. The platform control is keyboard-complete by
 * construction, opens on touch without a hover anywhere near it, and reads out its options to a
 * screen reader without a single aria attribute of ours. It wears the same `sd-qf` clothes every
 * other quiet field wears — borderless at rest, hairline on hover, brand ring on focus — so the
 * row is indistinguishable from its neighbours until it is used.
 *
 * NOTHING AUTOSAVES HERE EITHER. A changed selection marks the row dirty and the group's commit
 * bar is the only thing that writes.
 */

import type { SelectHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

interface Props extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
    /** The DOM id a manifest row's `jump.focus` names. */
    id: string;
    label: string;
    value: string;
    onValueChange: (value: string) => void;
    options: { value: string; label: string }[];
    /** What an unset value reads as. Omit where the field always holds one of the options. */
    placeholder?: string;
    /** The quiet line that appears on focus, same as a quiet field's. */
    format?: string;
    /** The server's own refusal for this field, verbatim. */
    error?: string | null;
    dirty?: boolean;
    /**
     * A group in flight does not take edits — the write already left with these values.
     *
     * IT DISABLES RATHER THAN GOING READ-ONLY because a `<select>` has no readonly state: the
     * attribute is silently ignored and the list opens anyway. The prop keeps its name so a group
     * field spreads onto this the same way it spreads onto a quiet field.
     */
    readOnly?: boolean;
}

export default function QuietSelect({
    id,
    label,
    value,
    onValueChange,
    options,
    placeholder,
    format,
    error,
    dirty,
    readOnly,
    disabled,
    className,
    ...rest
}: Props) {
    return (
        <div className={cn('sd-fld', dirty && 'sd-dirty', error && 'sd-errored')}>
            <label className="sd-fld-k" htmlFor={id}>
                {label}
            </label>
            <div className="flex min-w-0 items-center gap-2">
                <select
                    {...rest}
                    id={id}
                    className={cn('sd-qf', className)}
                    value={value}
                    disabled={disabled || readOnly}
                    onChange={(event) => onValueChange(event.target.value)}
                    aria-invalid={error ? true : undefined}
                    aria-errormessage={error ? `${id}-err` : undefined}
                >
                    {/* Only while nothing is chosen: an empty option that survives a choice is an
                        invitation to un-choose, which for a NOT NULL column is not an option. */}
                    {placeholder && value === '' && <option value="">{placeholder}</option>}
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {format && <span className="sd-fmt">{format}</span>}
            </div>
            {error && (
                <p className="sd-f-err" id={`${id}-err`}>
                    {error}
                </p>
            )}
        </div>
    );
}
